import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import theme from "../styles/theme";
import { adminResetPassword, deleteData, getData } from "../api";

type UserRow = {
  id: string | number;
  username: string;
  role?: string;
  profile?: {
    nom_complet?: string;
    email?: string;
    equipe?: string;
  };
  activity?: {
    lastLoginAt?: string;
    lastSchemaViewAt?: string;
    schemaViews7d: number;
    schemaViewsAll: number;
    loginCountAll: number;
    quizAttempts: number;
    quizScoreSum: number;
  };
};

type ActivityEvent = {
  id?: number;
  event_type?: string;
  user_id?: string | number;
  username?: string;
  schema_uid?: string;
  schema_name?: string;
  origin?: string;
  success?: number | boolean;
  duration_ms?: number | null;
  metadata?: string | null;
  created_at?: string;
};

const generatePassword = (length: number = 10) => {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const ROLE_LABELS: Record<string, string> = {
  player: "Joueuse",
  coach: "Coach",
  staff: "Staff",
  superadmin: "Super Admin",
};

const ROLE_COLORS: Record<string, string> = {
  player: "#3b82f6",
  coach: "#10b981",
  staff: "#f59e0b",
  superadmin: "#ef4444",
};

const EVENT_LABELS: Record<string, (ev: ActivityEvent) => string> = {
  login_success: () => "Connexion réussie",
  login_failed: (ev) => {
    try {
      const m = ev.metadata ? (typeof ev.metadata === "string" ? JSON.parse(ev.metadata) : ev.metadata) : {};
      return `Tentative échouée${(m as any)?.reason === "invalid_password" ? " (mauvais mot de passe)" : (m as any)?.reason === "user_not_found" ? " (utilisateur inconnu)" : ""}`;
    } catch { return "Tentative échouée"; }
  },
  schema_play: (ev) => `▶ Schéma joué : ${ev.schema_name || ev.schema_uid || "—"}`,
  schema_view_open: (ev) => `▶ Schéma joué : ${ev.schema_name || ev.schema_uid || "—"}`,
  schema_play_end: (ev) => {
    const sec = ev.duration_ms != null ? Math.round(Number(ev.duration_ms) / 1000) : null;
    return `⏹ Schéma fermé : ${ev.schema_name || ev.schema_uid || "—"}${sec != null ? ` (${sec}s)` : ""}`;
  },
  schema_view_close: (ev) => {
    const sec = ev.duration_ms != null ? Math.round(Number(ev.duration_ms) / 1000) : null;
    return `Schéma fermé : ${ev.schema_name || ev.schema_uid || "—"}${sec != null ? ` (${sec}s)` : ""}`;
  },
  admin_password_reset: () => "Réinitialisation mot de passe (admin)",
  quiz_attempt: (ev) => {
    try {
      const m = ev.metadata ? (typeof ev.metadata === "string" ? JSON.parse(ev.metadata) : ev.metadata) : {};
      const score = (m as any)?.score;
      return `🧩 Quiz : ${ev.schema_name || ev.schema_uid || "—"}${score != null ? ` — ${score}%` : ""}`;
    } catch { return `🧩 Quiz : ${ev.schema_name || ev.schema_uid || "—"}`; }
  },
};

const EVENT_COLORS: Record<string, string> = {
  login_success: "#10b981",
  login_failed: "#ef4444",
  schema_play: "#3b82f6",
  schema_play_end: "#6366f1",
  schema_view_open: "#3b82f6",
  schema_view_close: "#6366f1",
  admin_password_reset: "#f59e0b",
  quiz_attempt: "#8b5cf6",
};

function eventLabel(ev: ActivityEvent): string {
  const fn = EVENT_LABELS[ev.event_type ?? ""];
  return fn ? fn(ev) : ev.event_type ?? "Événement";
}

export default function SuperAdminPasswords() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Reset password dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPlayer, setResetPlayer] = useState<UserRow | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Activity detail modal
  const [detailUser, setDetailUser] = useState<UserRow | null>(null);
  const [detailEvents, setDetailEvents] = useState<ActivityEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Purge des logs de quiz
  const [clearQuizOpen, setClearQuizOpen] = useState(false);
  const [clearingQuiz, setClearingQuiz] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; severity: "success" | "error"; message: string }>({
    open: false,
    severity: "success",
    message: "",
  });

  const canUseClipboard = useMemo(() => Boolean(navigator?.clipboard?.writeText), []);

  const fmtDate = (iso?: string) => {
    if (!iso) return "-";
    const d = new Date(iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const isRecent = (iso?: string, hours = 72) => {
    if (!iso) return false;
    const d = new Date(iso.replace(" ", "T"));
    return !Number.isNaN(d.getTime()) && Date.now() - d.getTime() <= hours * 3600000;
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [resUsers, resEvents]: any = await Promise.all([
        getData("users", { order_by: "role ASC, username ASC", limit: 1000 }),
        getData("activity_events", {
          where: "event_type IN ('login_success','login_failed','schema_play','schema_play_end','schema_view_open','schema_view_close','quiz_attempt')",
          order_by: "created_at DESC",
          limit: 5000,
        }),
      ]);

      const allUsers = Array.isArray(resUsers?.data) ? resUsers.data : [];
      const events: ActivityEvent[] = Array.isArray(resEvents?.data) ? resEvents.data : [];

      const byKey = new Map<
        string,
        { lastLoginAt?: string; lastSchemaViewAt?: string; schemaViews7d: number; schemaViewsAll: number; loginCountAll: number; quizAttempts: number; quizScoreSum: number }
      >();
      const cutoff7d = Date.now() - 7 * 24 * 3600000;

      events.forEach((ev) => {
        const keys = [String(ev.user_id ?? "").trim(), String(ev.username ?? "").trim()].filter(Boolean);
        keys.forEach((key) => {
          const createdAt = ev.created_at ? new Date(ev.created_at.replace(" ", "T")) : null;
          const iso = ev.created_at ? String(ev.created_at) : undefined;
          const e = byKey.get(key) || { schemaViews7d: 0, schemaViewsAll: 0, loginCountAll: 0, quizAttempts: 0, quizScoreSum: 0 };

          if (ev.event_type === "login_success") {
            if (!e.lastLoginAt && iso) e.lastLoginAt = iso;
            e.loginCountAll++;
          }
          if (ev.event_type === "schema_play" || ev.event_type === "schema_view_open") {
            if (!e.lastSchemaViewAt && iso) e.lastSchemaViewAt = iso;
            e.schemaViewsAll++;
            if (createdAt && !Number.isNaN(createdAt.getTime()) && createdAt.getTime() >= cutoff7d) e.schemaViews7d++;
          }
          if (ev.event_type === "quiz_attempt") {
            let score = 0;
            try {
              const m = ev.metadata ? (typeof ev.metadata === "string" ? JSON.parse(ev.metadata) : ev.metadata) : {};
              score = Number((m as any)?.score) || 0;
            } catch { /* score reste 0 si metadata illisible */ }
            e.quizAttempts++;
            e.quizScoreSum += score;
          }
          byKey.set(key, e);
        });
      });

      const rows: UserRow[] = await Promise.all(
        allUsers.map(async (u: any) => {
          const profileRes: any = await getData("profiles", { where: `user_id = '${u.id}'`, limit: 1 });
          const profile = profileRes?.data?.[0] || {};
          const act = byKey.get(String(u.id)) || byKey.get(String(u.username)) || { schemaViews7d: 0, schemaViewsAll: 0, loginCountAll: 0, quizAttempts: 0, quizScoreSum: 0 };
          return { id: u.id, username: u.username, role: u.role, profile, activity: act };
        })
      );

      setUsers(rows);
    } catch (e: any) {
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur chargement." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Open detail modal and fetch events for that user ---
  const openDetail = async (u: UserRow) => {
    setDetailUser(u);
    setDetailEvents([]);
    setDetailLoading(true);
    try {
      const whereById = `user_id = '${String(u.id).replace(/'/g, "''")}'`;
      const whereByUsername = `username = '${String(u.username).replace(/'/g, "''")}'`;
      const res: any = await getData("activity_events", {
        where: `(${whereById} OR ${whereByUsername})`,
        order_by: "created_at DESC",
        limit: 200,
      });
      setDetailEvents(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setDetailEvents([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // --- Reset password ---
  const openReset = (p: UserRow) => { setResetPlayer(p); setNewPassword(generatePassword()); setResetOpen(true); };
  const closeReset = () => { setResetOpen(false); setResetPlayer(null); setNewPassword(""); };

  const doReset = async () => {
    if (!resetPlayer || !newPassword.trim()) return;
    try {
      await adminResetPassword(resetPlayer.id, newPassword);
      setSnack({ open: true, severity: "success", message: `MDP réinitialisé pour ${resetPlayer.username}.` });
      closeReset();
    } catch (e: any) {
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur réinitialisation." });
    }
  };

  const copyPassword = async () => {
    if (!canUseClipboard) return;
    try { await navigator.clipboard.writeText(newPassword); setSnack({ open: true, severity: "success", message: "Mot de passe copié." }); }
    catch { setSnack({ open: true, severity: "error", message: "Impossible de copier." }); }
  };

  // --- Purge des logs de quiz (irréversible) ---
  const clearQuizLogs = async () => {
    setClearingQuiz(true);
    try {
      const res: any = await deleteData("activity_events", "event_type='quiz_attempt'");
      setSnack({ open: true, severity: "success", message: `${res?.deleted ?? 0} log(s) de quiz supprimé(s).` });
      setClearQuizOpen(false);
      await loadUsers();
      if (detailUser) await openDetail(detailUser);
    } catch (e: any) {
      setSnack({ open: true, severity: "error", message: e?.message || "Erreur lors de la suppression des logs de quiz." });
    } finally {
      setClearingQuiz(false);
    }
  };

  // Group detail events by type for the summary
  const detailSummary = useMemo(() => {
    const counts: Record<string, number> = {};
    detailEvents.forEach((ev) => { const t = ev.event_type ?? "?"; counts[t] = (counts[t] || 0) + 1; });
    return counts;
  }, [detailEvents]);

  // Bar chart data: plays par schéma (schema_play + rétrocompat schema_view_open)
  const schemaBarData = useMemo(() => {
    const counts = new Map<string, number>();
    detailEvents.forEach((ev) => {
      if (ev.event_type !== "schema_play" && ev.event_type !== "schema_view_open") return;
      const name = ev.schema_name || ev.schema_uid || "(sans nom)";
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, vues]) => ({ name, vues }))
      .sort((a, b) => b.vues - a.vues);
  }, [detailEvents]);

  // Bar chart data : score moyen au quiz par schéma
  const quizBarData = useMemo(() => {
    const sums = new Map<string, { sum: number; count: number }>();
    detailEvents.forEach((ev) => {
      if (ev.event_type !== "quiz_attempt") return;
      const name = ev.schema_name || ev.schema_uid || "(sans nom)";
      let score = 0;
      try {
        const m = ev.metadata ? (typeof ev.metadata === "string" ? JSON.parse(ev.metadata) : ev.metadata) : {};
        score = Number((m as any)?.score) || 0;
      } catch { /* score reste 0 si metadata illisible */ }
      const e = sums.get(name) || { sum: 0, count: 0 };
      e.sum += score;
      e.count++;
      sums.set(name, e);
    });
    return Array.from(sums.entries())
      .map(([name, { sum, count }]) => ({ name, score: Math.round(sum / count), tentatives: count }))
      .sort((a, b) => b.score - a.score);
  }, [detailEvents]);

  // Positions "validées" : au moins une tentative de quiz à 85% ou plus sur le schéma.
  const VALIDATION_THRESHOLD = 85;
  const validatedSchemas = useMemo(() => {
    const map = new Map<string, { bestScore: number; attempts: number; validatedAt: string | null }>();
    detailEvents.forEach((ev) => {
      if (ev.event_type !== "quiz_attempt") return;
      const name = ev.schema_name || ev.schema_uid || "(sans nom)";
      let score = 0;
      try {
        const m = ev.metadata ? (typeof ev.metadata === "string" ? JSON.parse(ev.metadata) : ev.metadata) : {};
        score = Number((m as any)?.score) || 0;
      } catch { /* score reste 0 si metadata illisible */ }
      const e = map.get(name) || { bestScore: 0, attempts: 0, validatedAt: null };
      e.attempts++;
      if (score > e.bestScore) e.bestScore = score;
      if (score >= VALIDATION_THRESHOLD && ev.created_at && (!e.validatedAt || ev.created_at < e.validatedAt)) {
        e.validatedAt = ev.created_at;
      }
      map.set(name, e);
    });
    return Array.from(map.entries())
      .filter(([, e]) => e.bestScore >= VALIDATION_THRESHOLD)
      .map(([name, e]) => ({ name, ...e }))
      .sort((a, b) => (a.validatedAt || "").localeCompare(b.validatedAt || ""));
  }, [detailEvents]);

  return (
    <Box sx={{ p: 3, pb: 10 }}>
      <Typography variant="h4" sx={{ mb: 1, color: theme.palette.primary.main, fontWeight: 800 }}>
        Passwords & Tracking — Tous les utilisateurs
      </Typography>
      <Typography sx={{ mb: 3, color: theme.palette.text.secondary }}>
        Coachs, staff et joueuses. Cliquer sur <b>Détail</b> pour voir l'historique complet d'activité (connexions, schémas consultés…).
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={loadUsers} disabled={loading}>
          {loading ? "Chargement…" : "Rafraîchir"}
        </Button>
        <Button variant="outlined" color="error" onClick={() => setClearQuizOpen(true)}>
          🧩 Vider les logs Quiz
        </Button>
      </Box>

      <Box sx={{ overflowX: "auto", background: theme.palette.background.paper, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8fafc" }}>
              <TableCell><b>Rôle</b></TableCell>
              <TableCell><b>Login</b></TableCell>
              <TableCell><b>Nom</b></TableCell>
              <TableCell><b>Équipe</b></TableCell>
              <TableCell><b>Dernière connexion</b></TableCell>
              <TableCell><b>Connexions (total)</b></TableCell>
              <TableCell><b>Plays schéma (7j)</b></TableCell>
              <TableCell><b>Plays schéma (total)</b></TableCell>
              <TableCell><b>Quiz</b></TableCell>
              <TableCell align="right"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={String(u.id)} hover>
                <TableCell>
                  <Chip
                    label={ROLE_LABELS[u.role ?? ""] ?? u.role ?? "?"}
                    size="small"
                    sx={{ bgcolor: (ROLE_COLORS[u.role ?? ""] ?? "#94a3b8") + "22", color: ROLE_COLORS[u.role ?? ""] ?? "#94a3b8", fontWeight: 700, fontSize: "0.75rem" }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                <TableCell>{u.profile?.nom_complet || "—"}</TableCell>
                <TableCell>{u.profile?.equipe || "—"}</TableCell>
                <TableCell>
                  <Tooltip title={u.activity?.lastLoginAt ?? "Jamais"}>
                    <Typography sx={{ fontSize: "0.82rem", color: isRecent(u.activity?.lastLoginAt) ? "success.main" : "text.secondary", fontWeight: isRecent(u.activity?.lastLoginAt) ? 700 : 400 }}>
                      {fmtDate(u.activity?.lastLoginAt)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: (u.activity?.loginCountAll ?? 0) > 0 ? "success.main" : "text.disabled" }}>
                    {u.activity?.loginCountAll ?? 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: (u.activity?.schemaViews7d ?? 0) > 0 ? "primary.main" : "text.disabled" }}>
                    {u.activity?.schemaViews7d ?? 0} ▶
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: (u.activity?.schemaViewsAll ?? 0) > 0 ? "primary.main" : "text.disabled" }}>
                    {u.activity?.schemaViewsAll ?? 0} ▶
                  </Typography>
                </TableCell>
                <TableCell>
                  {(u.activity?.quizAttempts ?? 0) > 0 ? (
                    <Tooltip title="Voir le détail des tentatives de quiz">
                      <Typography
                        onClick={() => openDetail(u)}
                        sx={{ fontSize: "0.9rem", fontWeight: 700, color: "secondary.main", cursor: "pointer", textDecoration: "underline", width: "fit-content" }}
                      >
                        {Math.round((u.activity?.quizScoreSum ?? 0) / (u.activity?.quizAttempts ?? 1))}% ({u.activity?.quizAttempts})
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography sx={{ fontSize: "0.9rem", color: "text.disabled" }}>—</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <Button size="small" variant="outlined" color="info" onClick={() => openDetail(u)}>
                      Détail
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => openReset(u)}>
                      MDP
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography sx={{ p: 2, color: theme.palette.text.secondary }}>Aucun utilisateur trouvé.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* === MODAL DÉTAIL ACTIVITÉ === */}
      <Dialog open={Boolean(detailUser)} onClose={() => setDetailUser(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Activité de {detailUser?.username}</Typography>
            {detailUser?.role && (
              <Chip
                label={ROLE_LABELS[detailUser.role] ?? detailUser.role}
                size="small"
                sx={{ bgcolor: (ROLE_COLORS[detailUser.role] ?? "#94a3b8") + "22", color: ROLE_COLORS[detailUser.role] ?? "#94a3b8", fontWeight: 700 }}
              />
            )}
          </Box>
          {detailUser?.profile?.nom_complet && (
            <Typography variant="body2" color="text.secondary">{detailUser.profile.nom_complet}{detailUser.profile.equipe ? ` — ${detailUser.profile.equipe}` : ""}</Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Résumé */}
          {!detailLoading && detailEvents.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, mt: 1 }}>
              {Object.entries(detailSummary).map(([type, count]) => (
                <Chip
                  key={type}
                  size="small"
                  label={`${EVENT_LABELS[type]?.({ event_type: type } as ActivityEvent) ?? type} × ${count}`}
                  sx={{ bgcolor: (EVENT_COLORS[type] ?? "#94a3b8") + "18", color: EVENT_COLORS[type] ?? "#94a3b8", fontWeight: 700, fontSize: "0.72rem" }}
                />
              ))}
            </Box>
          )}

          {/* Graphique barres : vues par schéma */}
          {!detailLoading && schemaBarData.length > 0 && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1 }}>
                Schémas joués (total)
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(120, schemaBarData.length * 36)}>
                <BarChart
                  data={schemaBarData}
                  layout="vertical"
                  margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v: any) => [`${v} play${v > 1 ? "s" : ""}`, "Lectures"]} />
                  <Bar dataKey="vues" radius={[0, 4, 4, 0]}>
                    {schemaBarData.map((_, i) => (
                      <Cell key={i} fill={["#3b82f6", "#6366f1", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444"][i % 6]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Graphique barres : score moyen au quiz par schéma */}
          {!detailLoading && quizBarData.length > 0 && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1 }}>
                🧩 Score moyen au Quiz Placement (par schéma)
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(120, quizBarData.length * 36)}>
                <BarChart data={quizBarData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v: any, _n: any, p: any) => [`${v}% (${p?.payload?.tentatives} tentative${p?.payload?.tentatives > 1 ? "s" : ""})`, "Score moyen"]} />
                  <Bar dataKey="score" radius={[0, 4, 4, 0]} fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Tableau des positions validées (score quiz ≥ 85%) */}
          {!detailLoading && validatedSchemas.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1 }}>
                ✅ Positions validées (score ≥ {VALIDATION_THRESHOLD}%)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Schéma</b></TableCell>
                    <TableCell><b>Meilleur score</b></TableCell>
                    <TableCell><b>Tentatives</b></TableCell>
                    <TableCell><b>Validé le</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validatedSchemas.map((s) => (
                    <TableRow key={s.name}>
                      <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={`${s.bestScore}%`} sx={{ bgcolor: "#22c55e22", color: "#16a34a", fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>{s.attempts}</TableCell>
                      <TableCell>{fmtDate(s.validatedAt || undefined)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : detailEvents.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              Aucune activité enregistrée pour cet utilisateur.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {detailEvents.map((ev, i) => {
                const color = EVENT_COLORS[ev.event_type ?? ""] ?? "#94a3b8";
                const isFail = ev.event_type === "login_failed";
                return (
                  <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start", py: 0.75, px: 1, borderRadius: 1, "&:hover": { bgcolor: "#f8fafc" } }}>
                    {/* Dot */}
                    <Box sx={{ mt: "6px", width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0, boxShadow: `0 0 0 3px ${color}22` }} />
                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: isFail ? 700 : 500, color: isFail ? "error.main" : "text.primary", lineHeight: 1.4 }}>
                        {eventLabel(ev)}
                      </Typography>
                      {ev.event_type?.startsWith("schema_view") && ev.schema_name && (
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", fontFamily: "monospace" }}>
                          uid: {ev.schema_uid ?? "—"}
                        </Typography>
                      )}
                    </Box>
                    {/* Date */}
                    <Typography sx={{ fontSize: "0.75rem", color: "text.disabled", flexShrink: 0, mt: "2px" }}>
                      {fmtDate(ev.created_at)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailUser(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* === MODAL RESET MDP === */}
      <Dialog open={resetOpen} onClose={closeReset} maxWidth="sm" fullWidth>
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Nouveau mot de passe pour <b>{resetPlayer?.username}</b>. Note-le avant de confirmer, il ne pourra pas être récupéré ensuite.
          </DialogContentText>
          <TextField fullWidth label="Nouveau mot de passe" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "space-between", flexWrap: "wrap" }}>
            <Button variant="text" onClick={() => setNewPassword(generatePassword())}>Générer</Button>
            <Button variant="text" onClick={copyPassword} disabled={!canUseClipboard}>Copier</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReset}>Annuler</Button>
          <Button onClick={doReset} variant="contained" color="primary">Confirmer</Button>
        </DialogActions>
      </Dialog>

      {/* === MODAL PURGE LOGS QUIZ === */}
      <Dialog open={clearQuizOpen} onClose={() => (!clearingQuiz ? setClearQuizOpen(false) : undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Vider les logs de quiz</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action supprime <b>définitivement</b> tout l'historique des tentatives de Quiz Placement (tous les utilisateurs, toutes les équipes) — scores, tableaux de positions validées et graphiques associés disparaîtront. Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearQuizOpen(false)} disabled={clearingQuiz}>Annuler</Button>
          <Button onClick={clearQuizLogs} variant="contained" color="error" disabled={clearingQuiz}>
            {clearingQuiz ? "Suppression…" : "Supprimer définitivement"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
